import { motion } from 'framer-motion';

const INFORMAL_NOTE = [
  "I honestly did not want to make this boring legal stuff, but since this website uses Discord login, stores order records, and sends operational notifications to Big Country Energy management channels, it is better to be clear about it.",
  "This is not here to scare anyone. It is here so that if somebody ever decides to question how the site works, what data is used, or why orders and activity are logged, Big Country Energy has a clear answer instead of a messy problem.",
  "A template was used for the structure of this document, but the Big Country Energy-specific data, systems, and details were added manually.",
  "By using this website, you understand that this is a Big Country Energy system and that certain actions may be recorded for management, order handling, security, and operational purposes.",
];

const PRIVACY_SECTIONS = [
  {
    title: '1. Information We Collect',
    body: [
      'When you use the Big Country Energy website, we may collect information such as your Discord ID, Discord username, Discord display name, avatar, server roles, website login/session information, order details, in-game name, phone number, delivery or pickup preference, order notes, application answers, inventory activity, management review notes, timestamps, and related operational records.',
      'We only collect information that is needed to operate the website, process orders, manage applications, manage inventory, verify access, maintain records, and protect Big Country Energy systems from misuse.',
    ],
  },
  {
    title: '2. How We Use Information',
    body: [
      'Big Country Energy may use collected information to allow Discord login, verify Discord roles, process and manage orders, connect logged-in users to their order history, enforce limited-item purchase rules, review applications, manage inventory, send operational notifications to Discord management channels, resolve disputes, and protect the website from abuse.',
    ],
  },
  {
    title: '3. Orders and Applications',
    body: [
      'Order records may include your in-game name, phone number, selected items, quantities, total value, pickup or delivery choice, notes, order status, and Discord account association when you are logged in.',
      'Application records may include your Discord username, in-game name, age, timezone, experience, reason for joining, availability, application status, and management review notes.',
    ],
  },
  {
    title: '4. Discord Notifications',
    body: [
      'Certain website actions may be sent to private Big Country Energy Discord channels for management review. Examples include new orders, updated orders, inventory changes, low stock alerts, and new applications.',
      'These notifications are used for internal order handling, management, moderation, security, and operational recordkeeping.',
    ],
  },
  {
    title: '5. Cookies and Login Sessions',
    body: [
      'The website may use necessary cookies, tokens, or local storage to keep users logged in and make the website function properly.',
      'Big Country Energy does not use cookies for advertising or selling user data. If analytics, advertising, or tracking tools are added in the future, this Privacy Policy may be updated.',
    ],
  },
  {
    title: '6. Who Can See Your Information',
    body: [
      'Your information may be visible to Big Country Energy management, website administrators, authorized staff members, and private Discord channels used for Big Country Energy operations.',
      'Your information is not sold.',
    ],
  },
  {
    title: '7. Third-Party Services',
    body: [
      'Big Country Energy may use third-party services to operate the website, including Discord for login, identity, roles, and notifications, as well as database, storage, and hosting providers.',
      'These services may process information as needed to provide their services.',
    ],
  },
  {
    title: '8. Data Storage and Retention',
    body: [
      'Big Country Energy may keep records for as long as they are needed for order handling, application review, inventory records, management operations, dispute resolution, moderation, or security.',
      'Some records may be deleted when they are no longer needed, when a user leaves Big Country Energy, or when management decides they are no longer relevant.',
    ],
  },
  {
    title: '9. Data Deletion Requests',
    body: [
      'You may request that Big Country Energy delete or correct information related to you.',
      'Some records may need to be kept if they are required for order handling, application review, safety, dispute resolution, payment or delivery verification, or abuse prevention. To request deletion or correction, contact Big Country Energy management.',
    ],
  },
  {
    title: '10. Changes to This Policy',
    body: [
      'Big Country Energy may update this Privacy Policy at any time. If important changes are made, Big Country Energy may notify users through the website, Discord, or another reasonable method.',
    ],
  },
  {
    title: '11. Contact',
    body: ['For questions about this Privacy Policy, contact Big Country Energy management.'],
  },
];

const TERMS_SECTIONS = [
  {
    title: '1. Big Country Energy System',
    body: [
      'The Big Country Energy website is a system for customers, members, staff, and management. It may be used for orders, applications, Discord login, inventory management, announcements, management records, and Discord-related Big Country Energy operations.',
    ],
  },
  {
    title: '2. Discord Login',
    body: [
      'You may be required to log in with Discord to access certain features. By logging in, you allow the website to use your Discord account information, such as your Discord ID, username, avatar, and server roles, for Big Country Energy-related purposes.',
    ],
  },
  {
    title: '3. Orders',
    body: [
      'When you submit an order, you are responsible for making sure your in-game name, phone number, selected products, quantities, delivery or pickup choice, and notes are accurate.',
      'Orders may be logged in the website database and sent to Big Country Energy Discord management channels so staff can process and review them.',
    ],
  },
  {
    title: '4. Applications',
    body: [
      'When you submit an application, you are responsible for providing accurate and appropriate information. Big Country Energy management may approve, deny, review, or keep application records as needed for company operations.',
    ],
  },
  {
    title: '5. Activity Logging',
    body: [
      'Actions taken on the website may be logged. This includes orders, order edits, application submissions, management updates, inventory adjustments, Discord settings changes, and other important operational events.',
      'Some logs may be sent to Big Country Energy Discord management channels. These logs are used for operational records, moderation, security, and dispute resolution.',
    ],
  },
  {
    title: '6. User Responsibilities',
    body: [
      'You agree not to submit false information, place fake orders, abuse website features, attempt to access restricted areas, interfere with website systems, misuse Discord login or another user account, manipulate records without permission, or use the website in a way that harms Big Country Energy or its members.',
    ],
  },
  {
    title: '7. Management Decisions',
    body: [
      'Big Country Energy management may use website records, Discord notifications, order records, application records, inventory logs, and other internal information when making company decisions. This may include decisions about order fulfillment, application status, access permissions, inventory records, membership status, or disciplinary actions.',
    ],
  },
  {
    title: '8. No Guarantee of Access',
    body: [
      'Access to the Big Country Energy website may be changed, restricted, suspended, or removed at any time by management. Access may depend on your Discord roles, membership status, order status, or management approval.',
    ],
  },
  {
    title: '9. Website Availability',
    body: [
      'Big Country Energy will try to keep the website working, but the website may sometimes be unavailable due to maintenance, hosting issues, bugs, Discord issues, or other technical problems.',
      'Big Country Energy is not responsible for losses or issues caused by downtime or technical errors.',
    ],
  },
  {
    title: '10. Privacy',
    body: [
      'Your use of the website is also covered by the Big Country Energy Privacy Policy. By using the website, you understand that certain information and actions may be collected, stored, and logged for Big Country Energy purposes.',
    ],
  },
  {
    title: '11. Changes to These Terms',
    body: [
      'Big Country Energy may update these Terms at any time. If you continue using the website after changes are made, you accept the updated Terms.',
    ],
  },
  {
    title: '12. Contact',
    body: ['For questions about these Terms, contact Big Country Energy management.'],
  },
];

function LegalSection({ section }) {
  return (
    <section style={{
      borderTop: '1px solid var(--bce-border)',
      padding: '2rem 0',
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
      gap: '1.5rem',
    }}>
      <h2 style={{
        fontFamily: 'var(--font-condensed)',
        color: 'var(--bce-white)',
        fontSize: 22,
        lineHeight: 1.05,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
      }}>
        {section.title}
      </h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {section.body.map((paragraph) => (
          <p key={paragraph} style={{ color: 'var(--bce-text)', lineHeight: 1.85, fontSize: 15 }}>
            {paragraph}
          </p>
        ))}
      </div>
    </section>
  );
}

export default function Legal({ type = 'privacy' }) {
  const isTerms = type === 'terms';
  const title = isTerms ? 'Terms of Service' : 'Privacy Policy';
  const subtitle = isTerms
    ? 'Rules for using Big Country Energy orders, applications, and internal systems.'
    : 'How Big Country Energy collects, uses, stores, and shares website information.';
  const sections = isTerms ? TERMS_SECTIONS : PRIVACY_SECTIONS;

  return (
    <main className="bce-page" style={{
      background: 'var(--bce-bg)',
      padding: '4.5rem 1.5rem 6rem',
      position: 'relative',
      overflow: 'hidden',
    }}>
      <div style={{
        position: 'absolute',
        top: 'var(--header-h)',
        left: 0,
        right: 0,
        height: 12,
        backgroundImage: 'repeating-linear-gradient(-45deg, var(--bce-gold) 0, var(--bce-gold) 10px, #000 10px, #000 20px)',
        opacity: 0.9,
      }} />

      <div style={{ maxWidth: 1080, margin: '0 auto', position: 'relative' }}>
        <motion.div
          initial={{ opacity: 0, x: -18 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.45 }}
          style={{ paddingTop: '1.5rem' }}
        >
          <div className="bce-label" style={{ color: 'rgba(245,168,0,0.65)', marginBottom: '0.9rem' }}>
            Big Country Energy Notice
          </div>
          <h1 className="bce-display" style={{
            fontSize: 'clamp(3.5rem, 10vw, 8.5rem)',
            color: 'var(--bce-white)',
            marginBottom: '1.5rem',
          }}>
            {title}
          </h1>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: '1.5rem',
            alignItems: 'end',
            borderBottom: '4px solid var(--bce-gold)',
            paddingBottom: '2rem',
          }}>
            <p style={{ color: 'var(--bce-text)', fontSize: 18, lineHeight: 1.75, maxWidth: 760 }}>
              {subtitle}
            </p>
            <div style={{ textAlign: 'right' }}>
              <div className="bce-label">Last Updated</div>
              <div className="bce-condensed" style={{ color: 'var(--bce-white)', fontSize: 24 }}>
                2026
              </div>
            </div>
          </div>
        </motion.div>

        <motion.aside
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, delay: 0.08 }}
          style={{
            margin: '2.5rem 0',
            background: 'var(--bce-bg-card)',
            border: '1px solid var(--bce-gold-border)',
            borderLeft: '5px solid var(--bce-gold)',
            padding: '1.75rem',
            boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
          }}
        >
          <h2 className="bce-condensed" style={{ color: 'var(--bce-gold)', fontSize: 16, marginBottom: '1rem' }}>
            Informal Note
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {INFORMAL_NOTE.map((paragraph) => (
              <p key={paragraph} style={{ color: 'var(--bce-white)', lineHeight: 1.8, fontSize: 15 }}>
                {paragraph}
              </p>
            ))}
          </div>
        </motion.aside>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.45, delay: 0.14 }}
        >
          {sections.map((section) => (
            <LegalSection key={section.title} section={section} />
          ))}
        </motion.div>
      </div>
    </main>
  );
}
